import React from "react";
import { NavLink } from "react-router-dom";
import { LayoutDashboard, Users, LogOut, ChevronRight, ListChecks, LayoutTemplate, CalendarCheck, Settings as SettingsIcon, ScrollText, Clock, LayoutGrid, Search, Receipt, Contact, UserPlus, Briefcase, Library, HardDrive } from "lucide-react";
import { CATEGORY_META, CATEGORY_ORDER } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import NotificationBell from "@/components/NotificationBell";
import NotificationInbox from "@/components/NotificationInbox";
import ThemeToggle from "@/components/ThemeToggle";

const LOGO = "https://customer-assets-v7afamib.emergentagent.net/job_archi-interior-hub/artifacts/jmvnezz2_image.png";

const navItem = (active) =>
  `flex items-center gap-3 px-3 h-10 rounded-sm text-sm transition-colors ${
    active ? "bg-zinc-900 text-white font-semibold" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
  }`;

export function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const go = () => { if (onNavigate) onNavigate(); };

  return (
    <div className="flex flex-col h-full bg-zinc-50">
      <div className="p-5 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="TWP co" className="h-10 w-10 rounded-sm object-cover shrink-0" />
          <div>
            <p className="font-heading font-extrabold text-base leading-none tracking-tight">TWP co</p>
            <p className="text-[9px] uppercase tracking-[0.2em] text-zinc-400 mt-1">Bringing design to life</p>
          </div>
        </div>
      </div>

      <div className="px-3 pt-3">
        <button
          onClick={() => { window.dispatchEvent(new Event("twp:open-search")); go(); }}
          data-testid="sidebar-search-button"
          className="w-full inline-flex items-center gap-2 h-9 px-3 rounded-sm border border-zinc-200 bg-white text-sm text-zinc-500 hover:border-zinc-400 transition-colors"
        >
          <Search className="h-4 w-4" />
          Search…
          <kbd className="ml-auto text-[10px] font-sans text-zinc-400 border border-zinc-200 rounded px-1 py-0.5">⌘K</kbd>
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        <NavLink to="/" end onClick={go} className={({ isActive }) => navItem(isActive)} data-testid="nav-dashboard">
          <LayoutDashboard className="h-4 w-4" />
          Dashboard
        </NavLink>
        <NavLink to="/tasks" onClick={go} className={({ isActive }) => navItem(isActive)} data-testid="nav-tasks">
          <ListChecks className="h-4 w-4" />
          All Tasks
        </NavLink>
        <NavLink to="/board" onClick={go} className={({ isActive }) => navItem(isActive)} data-testid="nav-board">
          <LayoutGrid className="h-4 w-4" />
          Studio Board
        </NavLink>
        <NavLink to="/consultants" onClick={go} className={({ isActive }) => navItem(isActive)} data-testid="nav-consultants">
          <Briefcase className="h-4 w-4" />
          Consultants
        </NavLink>
        <NavLink to="/library" onClick={go} className={({ isActive }) => navItem(isActive)} data-testid="nav-library">
          <Library className="h-4 w-4" />
          Library
        </NavLink>
        <NavLink to="/files" onClick={go} className={({ isActive }) => navItem(isActive)} data-testid="nav-files">
          <HardDrive className="h-4 w-4" />
          Files
        </NavLink>
        <NavLink to="/my-week" onClick={go} className={({ isActive }) => navItem(isActive)} data-testid="nav-my-week">
          <CalendarCheck className="h-4 w-4" />
          My Week
        </NavLink>
        {user?.role === "admin" && (
          <NavLink to="/clients" onClick={go} className={({ isActive }) => navItem(isActive)} data-testid="nav-clients">
            <Contact className="h-4 w-4" />
            Clients
          </NavLink>
        )}

        <p className="px-3 pt-5 pb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Folders</p>
        {CATEGORY_ORDER.map((key) => {
          const meta = CATEGORY_META[key];
          const Icon = meta.icon;
          return (
            <NavLink
              key={key}
              to={`/folder/${key}`}
              onClick={go}
              className={({ isActive }) => navItem(isActive)}
              data-testid={`nav-folder-${key}`}
            >
              <Icon className="h-4 w-4" />
              <span className="flex-1 leading-tight">
                {meta.label}
                <span className="block text-[10px] uppercase tracking-wider opacity-60">{meta.sub}</span>
              </span>
              <ChevronRight className="h-3.5 w-3.5 opacity-40" />
            </NavLink>
          );
        })}

        {(user?.role === "admin" || user?.can_view_leads) && (
          <NavLink to="/crm" onClick={go} className={({ isActive }) => navItem(isActive)} data-testid="nav-crm">
            <UserPlus className="h-4 w-4" />
            Leads
          </NavLink>
        )}

        {user?.role === "admin" && (
          <>
            <p className="px-3 pt-5 pb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Admin</p>
            <NavLink to="/team" onClick={go} className={({ isActive }) => navItem(isActive)} data-testid="nav-team">
              <Users className="h-4 w-4" />
              Team & Permissions
            </NavLink>
            <NavLink to="/templates" onClick={go} className={({ isActive }) => navItem(isActive)} data-testid="nav-templates">
              <LayoutTemplate className="h-4 w-4" />
              Task Template
            </NavLink>
            <NavLink to="/settings" onClick={go} className={({ isActive }) => navItem(isActive)} data-testid="nav-settings">
              <SettingsIcon className="h-4 w-4" />
              Settings
            </NavLink>
            <NavLink to="/audit" onClick={go} className={({ isActive }) => navItem(isActive)} data-testid="nav-audit">
              <ScrollText className="h-4 w-4" />
              Audit Log
            </NavLink>
            <NavLink to="/time-report" onClick={go} className={({ isActive }) => navItem(isActive)} data-testid="nav-time-report">
              <Clock className="h-4 w-4" />
              Time Report
            </NavLink>
          </>
        )}

        {(user?.role === "admin" || user?.can_view_fees) && (
          <NavLink to="/fees-report" onClick={go} className={({ isActive }) => navItem(isActive)} data-testid="nav-fees-report">
            <Receipt className="h-4 w-4" />
            Fees Report
          </NavLink>
        )}
      </nav>

      <div className="p-3 border-t border-zinc-200">
        <div className="flex items-center gap-3 px-2 py-2">
          <Avatar id={user?.user_id} name={user?.name} size={32} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-[11px] text-zinc-500 truncate">
              {user?.role === "admin" ? "Admin" : user?.can_manage ? "Manager" : "Member"}
            </p>
          </div>
          <NotificationInbox />
          <NotificationBell />
          <ThemeToggle />
          <button
            onClick={() => { go(); logout(); }}
            data-testid="logout-button"
            className="h-8 w-8 flex items-center justify-center rounded-sm text-zinc-500 hover:bg-zinc-200 hover:text-zinc-900 transition-colors"
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Sidebar() {
  return (
    <aside className="hidden lg:flex w-64 shrink-0 border-r border-zinc-200 h-screen sticky top-0 flex-col">
      <SidebarContent />
    </aside>
  );
}
